<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { bringDrawLayersToTop } from '@/composables/useZLayers'

const emit = defineEmits(['click', 'mounted', 'loaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  source: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['fill', 'line', 'symbol', 'circle', 'heatmap', 'fill-extrusion', 'raster', 'hillshade', 'background', 'sky', 'slot'].includes(value),
  },
  options: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  beforeId: {
    type: String,
    required: false,
    default: null,
  },
  slot: {
    type: String,
    required: false,
    default: null,
  },
  groupId: {
    type: String,
    required: false,
    default: null,
  },
  interactive: {
    type: Boolean,
    required: false,
    default: true,
  },
})

// Throttle function to limit cursor updates
const throttle = (func, limit) => {
  let inThrottle
  return (...args) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

const throttledCursorUpdate = throttle((cursor) => {
  props.map.getCanvas().style.cursor = cursor
}, 50)

const addLayerListeners = (layer) => {
  props.map.on('mousemove', layer, (e) => {
    throttledCursorUpdate('pointer')
  })

  props.map.on('mouseleave', layer, () => {
    throttledCursorUpdate('default')
  })

  props.map.on('click', layer, (e) => {
    emit('click', e)
  })
}

const removeLayerListeners = (layer) => {
  props.map.off('mousemove', layer)
  props.map.off('mouseleave', layer)
  props.map.off('click', layer)
}

let sourceCheckInterval = null

const addLayer = async () => {
  if (!props.map.getLayer(props.id)) {
    const layerOptions = {
      id: props.id,
      type: props.type,
      ...props.options,
      minzoom: 0,
      maxzoom: 24,
    }

    if (props.slot) {
      layerOptions.slot = props.slot
    }

    if (props.source) {
      layerOptions.source = props.source
    }

    if (props.groupId) {
      layerOptions.metadata = {
        ...(layerOptions.metadata || {}),
        groupId: props.groupId,
      }
    }

    try {
      if (props.beforeId && props.map.getLayer(props.beforeId)) {
        props.map.addLayer(layerOptions, props.beforeId)
      } else {
        props.map.addLayer(layerOptions)
      }
    } catch (err) {
      const msg = err?.message ?? ''
      const transient = msg.includes('Style is not done loading') || msg.includes('Source') || msg.includes('style')
      if (!transient) throw err
      props.map.once('styledata', addLayer)
      return
    }

    bringDrawLayersToTop(props.map)

    // Only add listeners for interactive layers when interactive prop is true
    const nonInteractiveTypes = ['slot', 'background', 'sky', 'raster', 'hillshade']
    if (props.interactive && !nonInteractiveTypes.includes(props.type)) {
      addLayerListeners(props.id)
    }


    const waitForSourceLoaded = (map, sourceId, interval = 100) => {
      if (sourceCheckInterval) clearInterval(sourceCheckInterval)
      sourceCheckInterval = setInterval(() => {
        // The instance may be destroyed while polling (tile removed/recreated);
        // map.style is gone then and getSource would throw.
        if (!map.style || !map.getSource(sourceId)) {
          clearInterval(sourceCheckInterval)
          sourceCheckInterval = null
          return
        }

        if (map.isSourceLoaded(sourceId)) {
          clearInterval(sourceCheckInterval)
          sourceCheckInterval = null
          emit('loaded')
        }
      }, interval)
    }

    waitForSourceLoaded(props.map, props.source)
  } else {
    console.warn(`Layer '${props.id}' already exists on the map.`)
  }

  setTimeout(() => {
    emit('mounted')
  }, 1000)
}

const removeLayer = () => {
  // During tile teardown the parent MapCanvas may have already removed the
  // instance's style; getLayer() then dereferences an undefined style and throws.
  if (!props.map?.style) return
  try {
    if (props.map.getLayer(props.id)) {
      if (props.image) {
        props.map.removeImage(props.id)
      }
      props.map.removeLayer(props.id)
      removeLayerListeners(props.id)
    }
  } catch (_) { /* instance being disposed */ }
}

watch(() => props.options, (newOpts, oldOpts) => {
  if (!props.map?.getLayer(props.id)) return
  const newLayout = newOpts?.layout || {}
  const oldLayout = oldOpts?.layout || {}
  for (const [key, val] of Object.entries(newLayout)) {
    if (val !== oldLayout[key]) {
      try { props.map.setLayoutProperty(props.id, key, val) } catch (_) {}
    }
  }
  const newPaint = newOpts?.paint || {}
  const oldPaint = oldOpts?.paint || {}
  for (const [key, val] of Object.entries(newPaint)) {
    if (val !== oldPaint[key]) {
      try { props.map.setPaintProperty(props.id, key, val) } catch (_) {}
    }
  }
}, { deep: true })

// Watch for beforeId changes and move layer accordingly
// This is the key fix for layer ordering when drag-drop reorders layers
watch(() => props.beforeId, (newBeforeId, oldBeforeId) => {
  if (newBeforeId !== oldBeforeId && props.map && props.map.getLayer(props.id)) {
    // Determine the target layer to move before
    const targetLayer = newBeforeId && props.map.getLayer(newBeforeId)
      ? newBeforeId
      : undefined
    try {
      props.map.moveLayer(props.id, targetLayer)
      bringDrawLayersToTop(props.map)
    } catch (e) {
      console.warn(`Failed to move layer ${props.id}:`, e)
    }
  }
})

onMounted(async () => {
  await addLayer()
  props.map.on('style.load', addLayer)
})

onUnmounted(() => {
  if (sourceCheckInterval) {
    clearInterval(sourceCheckInterval)
    sourceCheckInterval = null
  }
  removeLayer()
  props.map.off('style.load', addLayer)
  props.map.off('styledata', addLayer)
})
</script>

<template>
  <slot></slot>
</template>
